import { useEffect, useMemo, useState } from "react";
import { CheckSquare, Shield, ChevronDown, ChevronRight, Check, AlertCircle, CheckCircle2 } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { authorizationService, screenService } from "../../services";

const uniqueScreens = (arr) =>
  arr.reduce((acc, curr) => {
    if (!acc.some((x) => x.screenId === curr.screenId)) acc.push(curr);
    return acc;
  }, []);

// ── Custom Checkbox ───────────────────────────────────────────────────────────
function Checkbox({ checked, indeterminate = false, onChange, size = "md" }) {
  const dim = size === "sm" ? "w-4 h-4" : "w-5 h-5";
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      onClick={onChange}
      className={`${dim} rounded flex-shrink-0 flex items-center justify-center border-2 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#1d57a5]/30 ${
        checked || indeterminate
          ? "bg-[#1d57a5] border-[#1d57a5]"
          : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-[#1d57a5]"
      }`}
    >
      {indeterminate && !checked ? (
        <span className="w-2 h-0.5 bg-white rounded-full" />
      ) : checked ? (
        <Check size={size === "sm" ? 10 : 12} strokeWidth={3} className="text-white" />
      ) : null}
    </button>
  );
}

// ── Screen Group Card ─────────────────────────────────────────────────────────
function ScreenGroupCard({ parent, children, selectedScreens, onParentToggle, onChildToggle }) {
  const [expanded, setExpanded] = useState(true);

  const parentChecked = selectedScreens.some((s) => s.screenId === parent.screenId);
  const checkedChildren = children.filter((c) => selectedScreens.some((s) => s.screenId === c.screenId));
  const indeterminate = !parentChecked && checkedChildren.length > 0;
  const allChildrenChecked = children.length > 0 && checkedChildren.length === children.length;

  return (
    <div className={`rounded-2xl border-2 transition-all duration-200 overflow-hidden ${
      parentChecked || indeterminate
        ? "border-[#1d57a5]/30 bg-[#1d57a5]/3 dark:bg-[#1d57a5]/5"
        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
    }`}>
      {/* Parent row */}
      <div className="flex items-center gap-3 px-4 py-3.5">
        <Checkbox
          checked={parentChecked || allChildrenChecked}
          indeterminate={indeterminate}
          onChange={() => onParentToggle(parent, children)}
        />
        <span
          className={`flex-1 text-sm font-semibold cursor-pointer select-none ${
            parentChecked || indeterminate
              ? "text-[#1d57a5] dark:text-[#4a8ce3]"
              : "text-slate-800 dark:text-slate-200"
          }`}
          onClick={() => onParentToggle(parent, children)}
        >
          {parent.screenName}
        </span>

        {/* Badge showing selected count */}
        {children.length > 0 && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            checkedChildren.length > 0
              ? "bg-[#1d57a5]/10 text-[#1d57a5] dark:text-[#4a8ce3]"
              : "bg-slate-100 dark:bg-slate-700 text-slate-500"
          }`}>
            {checkedChildren.length}/{children.length}
          </span>
        )}

        {/* Expand/collapse toggle */}
        {children.length > 0 && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        )}
      </div>

      {/* Children */}
      {children.length > 0 && expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-slate-700/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mt-2">
            {children.map((child) => {
              const childChecked = selectedScreens.some((s) => s.screenId === child.screenId);
              return (
                <label
                  key={child.screenId}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all select-none ${
                    childChecked
                      ? "bg-[#1d57a5]/8 dark:bg-[#1d57a5]/15 text-[#1d57a5] dark:text-[#4a8ce3]"
                      : "bg-slate-50 dark:bg-slate-900/40 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                  }`}
                  onClick={() => onChildToggle(parent, child, children)}
                >
                  <Checkbox
                    checked={childChecked}
                    onChange={() => onChildToggle(parent, child, children)}
                    size="sm"
                  />
                  <span className="text-xs font-medium truncate">{child.screenName}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AuthorizationPage() {
  const { groups } = useOutletContext();

  const [role, setRole]                     = useState("");
  const [parentScreens, setParentScreens]   = useState([]);
  const [childScreens, setChildScreens]     = useState([]);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [isSaving, setIsSaving]             = useState(false);
  const [message, setMessage]               = useState("");
  const [error, setError]                   = useState("");

  const createdBy = Number(localStorage.getItem("createdBy") || localStorage.getItem("userId") || 0);

  // Load all screens
  useEffect(() => {
    (async () => {
      try {
        const res = await screenService.getAll();
        const all = res?.data || [];
        setParentScreens(all.filter((x) => x.parentScreen == null || x.parentScreen === "NA"));
        setChildScreens(all.filter((x) => x.parentScreen != null && x.parentScreen !== "NA"));
      } catch (e) {
        setError(e?.message || "Failed to load screens.");
      }
    })();
  }, []);

  // Load role mapping when role changes
  useEffect(() => {
    if (!role) { setSelectedScreens([]); return; }
    setError("");
    (async () => {
      try {
        const res  = await authorizationService.getAll({ groupId: role });
        const data = Array.isArray(res?.data) ? res.data : [];
        const ids  = data[0]?.screenIds?.split(",").map(Number) || [];
        setSelectedScreens(ids.map((id) => ({ screenId: id, parentId: 0 })));
      } catch (e) {
        setError(e?.message || "Failed to load role mapping.");
      }
    })();
  }, [role]);

  const groupedScreens = useMemo(
    () => parentScreens.map((parent) => ({
      parent,
      children: childScreens.filter((c) => c.parentScreen === parent.screenName),
    })),
    [parentScreens, childScreens]
  );

  const handleParentToggle = (parent, children) => {
    const exists = selectedScreens.some((s) => s.screenId === parent.screenId);
    const childPayload = children.map((c) => ({ screenId: c.screenId, parentId: c.parentId || 0 }));
    const updated = exists
      ? selectedScreens.filter((s) => s.screenId !== parent.screenId && !children.some((c) => c.screenId === s.screenId))
      : [...selectedScreens, { screenId: parent.screenId, parentId: 0 }, ...childPayload];
    setSelectedScreens(uniqueScreens(updated));
  };

  const handleChildToggle = (parent, child, siblings) => {
    const exists = selectedScreens.some((s) => s.screenId === child.screenId);
    let updated  = exists
      ? selectedScreens.filter((s) => s.screenId !== child.screenId)
      : [...selectedScreens, { screenId: child.screenId, parentId: child.parentId || 0 }];

    const hasSibling = updated.some((s) => siblings.some((x) => x.screenId === s.screenId));
    if (hasSibling) {
      if (!updated.some((s) => s.screenId === parent.screenId))
        updated.push({ screenId: parent.screenId, parentId: 0 });
    } else {
      updated = updated.filter((s) => s.screenId !== parent.screenId);
    }
    setSelectedScreens(uniqueScreens(updated));
  };

  // Select / deselect all
  const handleSelectAll = () => {
    if (selectedScreens.length === parentScreens.length + childScreens.length) {
      setSelectedScreens([]);
    } else {
      const all = [
        ...parentScreens.map((p) => ({ screenId: p.screenId, parentId: 0 })),
        ...childScreens.map((c) => ({ screenId: c.screenId, parentId: c.parentId || 0 })),
      ];
      setSelectedScreens(uniqueScreens(all));
    }
  };

  const handleSave = async () => {
    if (!role) { setError("Please select a role."); return; }
    setIsSaving(true); setError(""); setMessage("");
    try {
      const res = await authorizationService.saveRoleScreenMapping({
        groupId: Number(role),
        createdBy,
        screens: selectedScreens,
      });
      setMessage(res?.status ? "Authorization mapping saved successfully." : "Saved.");
    } catch (e) {
      setError(e?.message || "Failed to save authorization mapping.");
    } finally {
      setIsSaving(false);
    }
  };

  const totalScreens   = parentScreens.length + childScreens.length;
  const selectedCount  = selectedScreens.length;
  const allSelected    = totalScreens > 0 && selectedCount === totalScreens;
  const someSelected   = selectedCount > 0 && !allSelected;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden flex flex-col max-h-[calc(100vh-140px)]">

      {/* ── Header ── */}
      <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1d57a5]/10 flex items-center justify-center">
            <Shield size={20} className="text-[#1d57a5]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-800 dark:text-white">Role Authorization Mapping</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Configure screen access permissions for each role</p>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">

        {/* Role selector + stats row */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="w-full sm:w-72">
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Select Role <span className="text-red-500">*</span>
            </label>
            <select
              value={role}
              onChange={(e) => setRole(Number(e.target.value))}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-[#1d57a5] outline-none transition-all appearance-none"
            >
              <option value="">— Choose a role —</option>
              {(groups || []).map((g) => (
                <option key={g.groupId} value={g.groupId}>{g.roleName}</option>
              ))}
            </select>
          </div>

          {/* Stats + select all */}
          {role && totalScreens > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                <span className="font-bold text-slate-800 dark:text-white">{selectedCount}</span> / {totalScreens} screens selected
              </span>
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-[#1d57a5] hover:text-[#1d57a5] transition-colors"
              >
                <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} size="sm" />
                {allSelected ? "Deselect All" : "Select All"}
              </button>
            </div>
          )}
        </div>

        {/* Screen groups */}
        {role && groupedScreens.length > 0 && (
          <div className="space-y-3">
            {groupedScreens.map(({ parent, children }) => (
              <ScreenGroupCard
                key={parent.screenId}
                parent={parent}
                children={children}
                selectedScreens={selectedScreens}
                onParentToggle={handleParentToggle}
                onChildToggle={handleChildToggle}
              />
            ))}
          </div>
        )}

        {/* Empty state when no role selected */}
        {!role && (
          <div className="text-center py-16 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
            <Shield size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Select a role to configure screen permissions</p>
          </div>
        )}

        {/* Feedback */}
        {error && (
          <div className="flex items-center gap-2.5 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 rounded-xl">
            <AlertCircle size={16} className="flex-shrink-0" /> {error}
          </div>
        )}
        {message && (
          <div className="flex items-center gap-2.5 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-4 py-3 rounded-xl">
            <CheckCircle2 size={16} className="flex-shrink-0" /> {message}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex-shrink-0 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/20">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {selectedCount > 0 ? `${selectedCount} screen${selectedCount !== 1 ? "s" : ""} will be assigned` : "No screens selected"}
        </p>
        <button
          onClick={handleSave}
          disabled={isSaving || !role}
          className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold bg-[#1d57a5] hover:bg-[#164280] disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white transition-all shadow-sm"
        >
          {isSaving ? (
            <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
          ) : (
            <><CheckSquare size={15} /> Save Authorization</>
          )}
        </button>
      </div>
    </div>
  );
}
